import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, DatePicker, Segmented, Input, message } from 'antd';
import api from '../api';

// Give a badge to an employee picked from `employees` ({ID, DisplayName,
// TeamName?}). Used on Admin > Badges (everyone) and Campaign Summary > Badges
// (the selected team). The API requires CanAssignBadge.
export default function AddEmployeeBadgeModal({ open, employees, onClose, onAdded }) {
  const [form] = Form.useForm();
  const [badges,     setBadges]     = useState([]);
  const [periodMode, setPeriodMode] = useState('month');
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setPeriodMode('month');
    api.get('/api/badges', { params: { active_only: 1 } })
      .then((r) => setBadges(r.data || []))
      .catch(() => {});
  }, [open, form]);

  const handleAdd = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      await api.post(`/api/users/${values.user_id}/badges`, {
        badge_id:   values.badge_id,
        year_month: values.year_month ? values.year_month.format(periodMode === 'year' ? 'YYYY' : 'YYYY-MM') : null,
        expires_on: values.expires_on ? values.expires_on.format('YYYY-MM-DD') : null,
        special_note: values.special_note || null,
      });
      message.success('Badge added.');
      onAdded?.();
      onClose();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maskClosable={false}
      title="Add Employee Badge"
      open={open}
      onOk={handleAdd}
      onCancel={onClose}
      confirmLoading={saving}
      okText="Add"
      destroyOnHidden
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        <Form.Item label="Employee" name="user_id" rules={[{ required: true, message: 'Required' }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={(employees || []).map((u) => ({
              value: u.ID,
              label: u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName,
            }))}
          />
        </Form.Item>
        <Form.Item label="Badge" name="badge_id" rules={[{ required: true, message: 'Required' }]}>
          <Select showSearch optionFilterProp="label" options={badges.map((b) => ({ value: b.ID, label: b.Name }))} />
        </Form.Item>
        <Form.Item label="Year-Month (optional)">
          <Segmented
            size="small"
            value={periodMode}
            onChange={(v) => { setPeriodMode(v); form.setFieldValue('year_month', null); }}
            options={[{ label: 'Month', value: 'month' }, { label: 'Year', value: 'year' }]}
            style={{ marginBottom: 8 }}
          />
          <Form.Item name="year_month" noStyle>
            <DatePicker picker={periodMode} format={periodMode === 'year' ? 'YYYY' : 'YYYY-MM'} style={{ width: '100%' }} />
          </Form.Item>
        </Form.Item>
        <Form.Item label="Expires On (optional)" name="expires_on">
          <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Special Note (optional)" name="special_note">
          <Input maxLength={100} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
